/* The Imagen half of the ad flows: composing placement frames (image mode)
   and carousel cards (carousel mode), plus the per-card refine / regenerate /
   stop machinery. The panel owns the brief and hands finished prompts down;
   this hook owns the generated output and its lifecycle. */
import { useRef, useState, type MutableRefObject } from "react"
import { toast } from "react-toastify"
import { useAdForgeGenerateImageMutation } from "~/ui-rtk/api/adForgeApi"
import { useAppSelector } from "~/ui-rtk/app/hooks"
import { selectAdForgeProductContext } from "~/ui-rtk/app/selectors/ad-forge.selector"
import { assetToScrapedImage, type ScrapedImage } from "../../../types"
import { resolveProductRef } from "../../../utils/product-ref"
import { FRAME_SLOTS } from "../constants"
import mediaRef from "../media-ref"
import {
  REFRAME_PROMPT,
  buildCardRefinePrompt,
  buildCarouselCardPrompt,
} from "../prompts"
import type { CarouselCard, FrameRatio } from "../types"

interface UseComposePipelineParams {
  /** The hero product shot — the parent every generated child hangs off, and
   *  the product-accuracy reference for derived carousel cards. */
  asset: ScrapedImage
  /** Call-to-action from the brief — the closing carousel card uses it. */
  cta: string
  cardCount: number
  carouselRatio: "1:1" | "4:5" | "9:16"
  /** Shared with the motion pipeline — flipped on unmount/cancel. */
  cancelledRef: MutableRefObject<boolean>
  /** Progress counter behind the status pill (frames or cards done). */
  setComposeDone: (done: number) => void
  /** Prompt + reference ids for a fresh card 1. Card 1 anchors the set's art
   *  direction, so regenerating it re-runs the whole brief — which only the
   *  panel can assemble. */
  buildFirstCardRun: () => { prompt: string; referenceIds: string[] }
}

export default function useComposePipeline({
  asset,
  cta,
  cardCount,
  carouselRatio,
  cancelledRef,
  setComposeDone,
  buildFirstCardRun,
}: UseComposePipelineParams) {
  // Stage-1 output: composed ad frames keyed by ratio (each persisted
  // server-side as a GEN_AI child of the source). In video mode nothing lands
  // here — Veo takes the references directly.
  const [composedFrames, setComposedFrames] = useState<
    Partial<Record<FrameRatio, ScrapedImage>>
  >({})
  // Carousel-mode output: the ordered card set (all persisted server-side as
  // GEN_AI children of the source, like the frames above).
  const [composedCards, setComposedCards] = useState<CarouselCard[]>([])

  const [generateImage] = useAdForgeGenerateImageMutation()

  // Which feed product this session is about: a Mega Feed-seeded hero's
  // synthetic id first, else the session's product context. Sent with every
  // generation so the output lands in that product's feed pool server-side.
  const productContext = useAppSelector(selectAdForgeProductContext)
  const productRefId = resolveProductRef(asset, productContext?.productId)

  // Compose the ad frame server-side in one ratio. The backend persists it as
  // a GEN_AI child of the source asset, tagged with its placement slot so the
  // launch flow can pick the right variant.
  const composeFrame = async (
    promptText: string,
    referenceAssetIds: string[],
    ratio: FrameRatio,
  ): Promise<ScrapedImage> => {
    const result = await generateImage({
      referenceAssetIds,
      prompt: promptText,
      aspectRatio: ratio,
      // Only a real library id can be a parent; URL-sourced heroes simply
      // produce an unparented GEN_AI asset.
      parentAssetId: asset.assetId,
      generatedBy: "ai_ad_frame",
      placementSlot: FRAME_SLOTS[ratio],
      ...(productRefId && { productRefId }),
    }).unwrap()
    if (!result.asset) {
      throw new Error("Frame composition returned no saved asset")
    }
    return assetToScrapedImage(result.asset)
  }

  // Compose the MASTER frame once with the full creative prompt, then
  // derive every other requested ratio FROM the master via the reframe
  // prompt — one art direction, recomposed per placement, so all frames
  // look like the same ad instead of unrelated generations. Runs in series
  // (parallel submissions risk throttling); a failed derived ratio is
  // tolerated, a failed master is fatal. `ratios` comes from the output
  // aspect-ratio selector.
  const composeAllFrames = async (
    promptText: string,
    referenceAssetIds: string[],
    ratios: FrameRatio[],
  ): Promise<Partial<Record<FrameRatio, ScrapedImage>>> => {
    const masterRatio: FrameRatio = ratios.includes("1:1") ? "1:1" : ratios[0]
    const frames: Partial<Record<FrameRatio, ScrapedImage>> = {}
    const failures: FrameRatio[] = []
    setComposeDone(0)
    const master = await composeFrame(
      promptText,
      referenceAssetIds,
      masterRatio,
    )
    frames[masterRatio] = master
    setComposedFrames(prev => ({ ...prev, [masterRatio]: master }))
    let done = 1
    setComposeDone(done)
    for (const ratio of ratios.filter(r => r !== masterRatio)) {
      if (cancelledRef.current) throw new Error("Cancelled")
      try {
        if (!master.assetId) {
          throw new Error("Master frame has no library asset id")
        }
        const frame = await composeFrame(
          REFRAME_PROMPT,
          [master.assetId],
          ratio,
        )
        frames[ratio] = frame
        setComposedFrames(prev => ({ ...prev, [ratio]: frame }))
      } catch {
        failures.push(ratio)
      }
      done += 1
      setComposeDone(done)
    }
    if (failures.length > 0) {
      toast.warning(
        `Reframing failed for ${failures.join(", ")} — continuing with the rest.`,
      )
    }
    return frames
  }

  // Carousel — compose card 1 with the full brief (it sets the campaign's
  // art direction), then derive each following card from it + the hero shot
  // so the set stays visually coherent. Series like the frames flow (same
  // throttling concern); a failed derived card is tolerated, a failed card 1
  // is fatal. `registerAbort` hands back a handle that cancels the HTTP
  // request — the per-card Stop button uses it.
  const composeCard = async (
    promptText: string,
    referenceAssetIds: string[],
    cardIndex: number,
    registerAbort?: (abort: () => void) => void,
  ): Promise<ScrapedImage> => {
    const request = generateImage({
      referenceAssetIds,
      prompt: promptText,
      aspectRatio: carouselRatio,
      parentAssetId: asset.assetId,
      generatedBy: `ai_carousel_card_${cardIndex}`,
      placementSlot: FRAME_SLOTS[carouselRatio],
      ...(productRefId && { productRefId }),
    })
    registerAbort?.(() => request.abort())
    const result = await request.unwrap()
    if (!result.asset) {
      throw new Error("Card composition returned no saved asset")
    }
    return assetToScrapedImage(result.asset)
  }

  const composeCarousel = async (
    firstCardPrompt: string,
    referenceAssetIds: string[],
  ): Promise<CarouselCard[]> => {
    const cards: CarouselCard[] = []
    const failures: number[] = []
    setComposeDone(0)
    setComposedCards([])
    const firstCard = await composeCard(firstCardPrompt, referenceAssetIds, 1)
    cards.push({ asset: firstCard, status: "ready" })
    setComposedCards([...cards])
    setComposeDone(1)
    for (let cardNumber = 2; cardNumber <= cardCount; cardNumber++) {
      if (cancelledRef.current) throw new Error("Cancelled")
      try {
        if (!firstCard.assetId) {
          throw new Error("Card 1 has no library asset id")
        }
        const heroRef = mediaRef(asset)
        const cardRefs = [firstCard.assetId, ...(heroRef ? [heroRef] : [])]
        const card = await composeCard(
          buildCarouselCardPrompt(cardNumber, cardCount, cta),
          cardRefs,
          cardNumber,
        )
        cards.push({ asset: card, status: "ready" })
        setComposedCards([...cards])
      } catch {
        failures.push(cardNumber)
      }
      setComposeDone(cardNumber)
    }
    if (failures.length > 0) {
      toast.warning(
        `Card${failures.length === 1 ? "" : "s"} ${failures.join(", ")} failed — continuing with the rest.`,
      )
    }
    return cards
  }

  // ── Per-card iteration (refine / regenerate one card at a time) ────────

  const hasActiveCard = composedCards.some(c => c.status === "generating")

  const setCardAt = (index: number, patch: Partial<CarouselCard>) =>
    setComposedCards(prev =>
      prev.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    )

  // Run tokens make per-card Stop race-safe: each refine/regenerate stamps
  // the card with a fresh run id; results only apply if the card still
  // carries that id. Stop deletes the id (and aborts the HTTP request), so
  // a late response from a stopped run is silently discarded — even if a
  // new run has started for the same card in the meantime.
  const cardRunSeqRef = useRef(0)
  const cardRunsRef = useRef(new Map<number, number>())
  const cardAbortsRef = useRef(
    new Map<number, { runId: number; abort: () => void }>(),
  )

  const startCardRun = (index: number) => {
    const runId = ++cardRunSeqRef.current
    cardRunsRef.current.set(index, runId)
    setCardAt(index, { status: "generating", error: undefined })
    return runId
  }

  const isCardRunCurrent = (index: number, runId: number) =>
    cardRunsRef.current.get(index) === runId

  const endCardRun = (index: number, runId: number) => {
    if (isCardRunCurrent(index, runId)) cardRunsRef.current.delete(index)
    if (cardAbortsRef.current.get(index)?.runId === runId) {
      cardAbortsRef.current.delete(index)
    }
  }

  // Stop: abort the in-flight request and put the card back to its last
  // ready version. From there ↻ restarts it from scratch.
  const stopCard = (index: number) => {
    cardRunsRef.current.delete(index)
    const held = cardAbortsRef.current.get(index)
    held?.abort()
    cardAbortsRef.current.delete(index)
    setCardAt(index, { status: "ready", error: undefined })
    toast.info(
      `Card ${index + 1} generation stopped — it kept its last version.`,
    )
  }

  // Refine: recompose THIS card from its own current image + the hero shot
  // (+ an optional reference image for the change), applying only the
  // user's instruction. Because the base is always the card's latest
  // version, repeated refinements stack — edit, look, edit again — without
  // disturbing the other cards.
  const refineCard = async (
    index: number,
    instruction: string,
    refImage?: ScrapedImage | null,
  ) => {
    const target = composedCards[index]
    if (!target || target.status === "generating" || !instruction.trim()) return
    // Composed cards are always persisted, so they have an id; fall back to
    // the URL anyway so a card can never become un-editable.
    const baseId = mediaRef(target.asset)
    if (!baseId) {
      toast.error("This card has no usable source")
      return
    }
    const runId = startCardRun(index)
    try {
      const refined = await composeCard(
        buildCardRefinePrompt(instruction, !!mediaRef(refImage ?? undefined)),
        [
          baseId,
          ...(mediaRef(asset) ? [mediaRef(asset) as string] : []),
          ...(mediaRef(refImage ?? undefined)
            ? [mediaRef(refImage ?? undefined) as string]
            : []),
        ],
        index + 1,
        abort => cardAbortsRef.current.set(index, { runId, abort }),
      )
      if (!isCardRunCurrent(index, runId)) return
      setCardAt(index, { asset: refined, status: "ready" })
      toast.success(`Card ${index + 1} updated`)
    } catch (e) {
      if (!isCardRunCurrent(index, runId)) return
      const errMsg = e instanceof Error ? e.message : "Card update failed"
      setCardAt(index, { status: "ready", error: errMsg })
      toast.error(errMsg)
    } finally {
      endCardRun(index, runId)
    }
  }

  // Regenerate: a fresh take on this card with the standard prompts — card 1
  // re-runs the full brief (it anchors the set's art direction), later cards
  // re-derive from the current card 1.
  const regenerateCard = async (index: number) => {
    const target = composedCards[index]
    if (!target || target.status === "generating") return
    const runId = startCardRun(index)
    const registerAbort = (abort: () => void) =>
      cardAbortsRef.current.set(index, { runId, abort })
    try {
      let fresh: ScrapedImage
      if (index === 0) {
        const { prompt, referenceIds } = buildFirstCardRun()
        fresh = await composeCard(prompt, referenceIds, 1, registerAbort)
      } else {
        const firstId = mediaRef(composedCards[0]?.asset)
        if (!firstId) throw new Error("Card 1 has no usable source")
        const heroRef = mediaRef(asset)
        fresh = await composeCard(
          buildCarouselCardPrompt(index + 1, cardCount, cta),
          [firstId, ...(heroRef ? [heroRef] : [])],
          index + 1,
          registerAbort,
        )
      }
      if (!isCardRunCurrent(index, runId)) return
      setCardAt(index, { asset: fresh, status: "ready" })
      toast.success(`Card ${index + 1} regenerated`)
    } catch (e) {
      if (!isCardRunCurrent(index, runId)) return
      const errMsg = e instanceof Error ? e.message : "Card regeneration failed"
      setCardAt(index, { status: "ready", error: errMsg })
      toast.error(errMsg)
    } finally {
      endCardRun(index, runId)
    }
  }

  return {
    composedFrames,
    setComposedFrames,
    composedCards,
    setComposedCards,
    hasActiveCard,
    composeAllFrames,
    composeCarousel,
    refineCard,
    regenerateCard,
    stopCard,
  }
}
